/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Divider,
  Alert,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  FormControlLabel,
  Switch,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import ProjectChat from './ProjectChat';
import { displayAccountNumber, displayPhoneNumber } from '../../utils/formatUtils';
import PhoneInput from './PhoneInput';
import { formatDateTime } from '../../utils/timeFormat';

const roundTo = (v) => Math.round((Number(v) || 0) * 100) / 100;

const CERTIFICATION_TEXT =
  'I certify that I have reviewed this invoice, agree that all charges are accurate, ' +
  'and verify that the account listed for payment is valid and authorized.';

const idOf = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v._id) return String(v._id);
  return String(v);
};

// Comparable shape so we can tell saved allocations from unsaved edits.
const normalizeAccounts = (list) =>
  JSON.stringify(
    (list || [])
      .map((a) => ({
        account_number: a.account_number,
        amount: roundTo(a.amount),
      }))
      .sort((a, b) => String(a.account_number).localeCompare(String(b.account_number)))
  );

/**
 * Step header shown in every accordion summary: a numbered badge that turns into
 * a tick once the step is done, or a padlock while the step is still locked.
 */
const StepHeader = ({ index, title, summary, done, locked }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: '0.85rem',
        fontWeight: 700,
        bgcolor: done ? 'success.main' : locked ? 'grey.300' : 'primary.main',
        color: locked && !done ? 'grey.600' : '#fff',
      }}
    >
      {done ? <CheckCircleIcon fontSize="small" /> : locked ? <LockIcon fontSize="small" /> : index}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Typography>
      {summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {summary}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * Dialog for the user-driven invoice distribution & signing flow.
 *
 * Presented as three gated steps: designate payers and choose accounts, confirm
 * the billing person the business office should contact, then sign. Each step
 * unlocks only once the previous one has been saved. A project chat panel sits
 * alongside so the team can discuss the split.
 */
const InvoiceDistributionDialog = ({ open, onClose, invoice, project, user, onUpdated }) => {
  const projectRef = project?.project_id || project?._id;
  const invoiceId = invoice?.invoice_id;
  const total = Number(invoice?.total_amount) || 0;
  const workflowStatus = invoice?.workflow_status;
  const readOnly = workflowStatus === 'Signed';

  const currentUserId = idOf(user?._id || user?.id);

  // Determine the current user's project role.
  const currentEntry = (project?.users || []).find(
    (u) => idOf(u.user) === currentUserId
  );
  const currentRole = currentEntry?.role?.role || currentEntry?.role;
  const isPI = currentRole === 'PI';

  // PI / Co-PI members that can be designated as payers.
  const eligibleMembers = (project?.users || []).filter((u) => {
    if (u.isDeleted) return false;
    const role = u.role?.role || u.role;
    return ['PI', 'Co-PI'].includes(role);
  });

  const [payers, setPayers] = useState([]); // PI editing rows
  const [savingSplit, setSavingSplit] = useState(false);

  const [myAccounts, setMyAccounts] = useState([]); // current user's saved accounts
  const [myAllocations, setMyAllocations] = useState([]); // [{ account_number, source, amount }]
  const [newAcctNumber, setNewAcctNumber] = useState('');
  const [newAcctSource, setNewAcctSource] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [savingShare, setSavingShare] = useState(false);
  const [signing, setSigning] = useState(false);
  const [unsigning, setUnsigning] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [editingSplit, setEditingSplit] = useState(false);

  // Billing person (step 2)
  const [billing, setBilling] = useState(null);
  const [billingSelf, setBillingSelf] = useState(true);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingConfirmed, setBillingConfirmed] = useState(false);

  const [expandedStep, setExpandedStep] = useState('step1');

  const existingPayers = invoice?.payer_distribution || [];
  const myPayer = existingPayers.find((p) => idOf(p.user_id) === currentUserId) || null;
  const splitIsSet = existingPayers.length > 0;
  const signedCount = existingPayers.filter((p) => p.confirmed).length;
  const allSigned = splitIsSet && signedCount === existingPayers.length;

  // Initialize the PI editing rows from eligible members + any existing split.
  useEffect(() => {
    if (!open) return;
    const rows = eligibleMembers.map((m) => {
      const uid = idOf(m.user);
      const existing = existingPayers.find((p) => idOf(p.user_id) === uid);
      const memberUser = typeof m.user === 'object' ? m.user : null;
      return {
        user_id: uid,
        display_name:
          `${m.first_name || memberUser?.first_name || ''} ${m.last_name || memberUser?.last_name || ''}`.trim() ||
          m.email ||
          'Unknown',
        project_role: m.role?.role || m.role,
        is_ncsu: memberUser?.is_ncsu !== undefined ? memberUser.is_ncsu : true,
        selected: !!existing,
        percent_share: existing ? String(existing.percent_share ?? '') : '',
      };
    });
    setPayers(rows);
    setEditingSplit(false);
  }, [open, invoice?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the current user's saved accounts.
  const loadMyAccounts = useCallback(async () => {
    if (!open || !user?.email) return;
    setLoadingAccounts(true);
    try {
      const res = await axios.get(`/api/users/with-accounts/${encodeURIComponent(user.email)}`);
      setMyAccounts(res.data?.account_numbers || []);
    } catch (err) {
      setMyAccounts(user?.account_numbers || []);
    } finally {
      setLoadingAccounts(false);
    }
  }, [open, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMyAccounts();
  }, [loadMyAccounts]);

  // Load the current user's billing person.
  const loadBillingContact = useCallback(async () => {
    if (!open) return;
    try {
      const res = await axios.get('/api/users/billing-contact');
      setBilling(res.data);
      setBillingSelf(res.data?.billing_contact?.is_self !== false);
    } catch (err) {
      console.error('Error loading billing contact:', err);
    }
  }, [open]);

  useEffect(() => {
    loadBillingContact();
  }, [loadBillingContact]);

  // Seed allocations from any previously saved accounts for this payer.
  useEffect(() => {
    if (!open) return;
    if (myPayer && Array.isArray(myPayer.accounts) && myPayer.accounts.length > 0) {
      setMyAllocations(
        myPayer.accounts.map((a) => ({
          account_number: a.account_number,
          source: a.source || 'Unknown',
          amount: String(a.amount ?? ''),
        }))
      );
    } else {
      setMyAllocations([]);
    }
    setSignatureName(myPayer?.signature?.name || '');
  }, [open, invoice?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time-ish refresh: poll for updates (e.g. other payers signing) while open.
  const onUpdatedRef = useRef(onUpdated);
  useEffect(() => {
    onUpdatedRef.current = onUpdated;
  });
  useEffect(() => {
    if (!open || readOnly) return undefined;
    const id = setInterval(() => {
      if (onUpdatedRef.current) onUpdatedRef.current();
    }, 12000);
    return () => clearInterval(id);
  }, [open, readOnly]);

  const totalPercent = payers
    .filter((p) => p.selected)
    .reduce((sum, p) => sum + (parseFloat(p.percent_share) || 0), 0);

  // ── Payer: account allocation ──
  const myShareAmount = roundTo(myPayer?.amount_share || 0);
  const allocatedTotal = roundTo(
    myAllocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0)
  );
  const remainingShare = roundTo(myShareAmount - allocatedTotal);
  const isAllocated = (accountNumber) =>
    myAllocations.some((a) => a.account_number === accountNumber);

  const isNonNcsuPayer = myPayer?.is_ncsu === false;
  const savedAccounts = myPayer?.accounts || [];
  const savedTotal = roundTo(savedAccounts.reduce((sum, a) => sum + (Number(a.amount) || 0), 0));
  const hasUnsavedAllocations =
    normalizeAccounts(myAllocations) !== normalizeAccounts(savedAccounts);
  const accountsSaved =
    savedAccounts.length > 0 && Math.abs(savedTotal - myShareAmount) < 0.01;

  // ── Step completion ──
  const step1Done =
    splitIsSet && !!myPayer && (isNonNcsuPayer || (accountsSaved && !hasUnsavedAllocations));
  const step2Done = step1Done && (billingConfirmed || !!myPayer?.confirmed);
  const step3Done = !!myPayer?.confirmed;

  // Open the first step that still needs attention when the dialog loads.
  const initialisedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialisedRef.current = false;
      return;
    }
    if (initialisedRef.current || !invoice) return;
    initialisedRef.current = true;
    if (myPayer?.confirmed) setExpandedStep('step3');
    else if (step1Done) setExpandedStep('step2');
    else setExpandedStep('step1');
  }, [open, invoice, myPayer, step1Done]);

  const handleTogglePayer = (uid) => {
    setPayers((prev) =>
      prev.map((p) => (p.user_id === uid ? { ...p, selected: !p.selected } : p))
    );
  };

  const handlePercentChange = (uid, value) => {
    setPayers((prev) =>
      prev.map((p) => (p.user_id === uid ? { ...p, percent_share: value } : p))
    );
  };

  const handleSaveSplit = async () => {
    const selected = payers.filter((p) => p.selected);
    if (selected.length === 0) {
      toast.error('Select at least one payer.');
      return;
    }
    if (Math.abs(totalPercent - 100) > 0.01) {
      toast.error(`Percentages must total 100% (currently ${totalPercent}%).`);
      return;
    }
    setSavingSplit(true);
    try {
      await axios.put(`/api/invoices/${projectRef}/${invoiceId}/distribution`, {
        payers: selected.map((p) => ({
          user_id: p.user_id,
          display_name: p.display_name,
          project_role: p.project_role,
          is_ncsu: p.is_ncsu,
          percent_share: parseFloat(p.percent_share) || 0,
        })),
      });
      toast.success('Payment split saved. Designated payers can now confirm and sign.');
      setEditingSplit(false);
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save split');
    } finally {
      setSavingSplit(false);
    }
  };

  const handleAddAllocation = (account) => {
    if (isAllocated(account.number)) {
      // Toggle off if already selected.
      setMyAllocations((prev) => prev.filter((a) => a.account_number !== account.number));
      return;
    }
    // Pre-fill the remaining amount so a single account auto-fills the full share.
    const suggested = remainingShare > 0 ? String(remainingShare) : '';
    setMyAllocations((prev) => [
      ...prev,
      {
        account_number: account.number,
        source: account.source || 'Unknown',
        amount: prev.length === 0 ? String(myShareAmount) : suggested,
      },
    ]);
  };

  const handleAllocationAmountChange = (index, value) => {
    setMyAllocations((prev) =>
      prev.map((a, i) => (i === index ? { ...a, amount: value } : a))
    );
  };

  const handleRemoveAllocation = (index) => {
    setMyAllocations((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddNewAccount = async () => {
    if (!newAcctNumber.trim() || !newAcctSource.trim()) {
      toast.error('Enter both account number and source of funds.');
      return;
    }
    try {
      await axios.post(`/api/users/${currentUserId}/account-numbers`, {
        number: newAcctNumber.trim(),
        source: newAcctSource.trim(),
      });
      toast.success('Account saved to your profile.');
      const added = { number: newAcctNumber.trim(), source: newAcctSource.trim() };
      setNewAcctNumber('');
      setNewAcctSource('');
      await loadMyAccounts();
      handleAddAllocation(added);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add account');
    }
  };

  const buildAccountsPayload = () =>
    myAllocations
      .filter((a) => a.account_number)
      .map((a) => ({
        account_number: a.account_number,
        source: a.source,
        amount: parseFloat(a.amount) || 0,
      }));

  const handleSaveMyShare = async () => {
    if (buildAccountsPayload().length === 0) {
      toast.error('Select at least one account.');
      return;
    }
    if (Math.abs(allocatedTotal - myShareAmount) > 0.01) {
      toast.error(`Account amounts must total your share of $${myShareAmount.toFixed(2)}.`);
      return;
    }
    setSavingShare(true);
    try {
      await axios.put(`/api/invoices/${projectRef}/${invoiceId}/distribution/my-share`, {
        accounts: buildAccountsPayload(),
      });
      toast.success('Accounts saved.');
      if (onUpdated) await onUpdated();
      setExpandedStep('step2');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save accounts');
    } finally {
      setSavingShare(false);
    }
  };

  const handleBillingFieldChange = (field, value) => {
    setBilling((prev) => ({
      ...prev,
      billing_contact: { ...(prev?.billing_contact || {}), is_self: false, [field]: value },
    }));
    setBillingConfirmed(false);
  };

  const handleToggleBillingSelf = (checked) => {
    setBillingSelf(checked);
    setBillingConfirmed(false);
    setBilling((prev) => ({
      ...prev,
      billing_contact: checked
        ? { is_self: true, name: '', email: '', contact_number: '' }
        : { ...(prev?.billing_contact || {}), is_self: false },
    }));
  };

  const handleSaveBillingContact = async () => {
    const bc = billing?.billing_contact || {};
    if (!billingSelf) {
      if (!bc.name?.trim()) {
        toast.error('Enter the billing person\'s name.');
        return;
      }
      if (!bc.email?.trim() || !/.+@.+\..+/.test(bc.email.trim())) {
        toast.error('Enter a valid billing person email.');
        return;
      }
    }
    setSavingBilling(true);
    try {
      const res = await axios.put('/api/users/billing-contact', {
        billing_contact: billingSelf
          ? { is_self: true }
          : {
              is_self: false,
              name: bc.name,
              email: bc.email,
              contact_number: bc.contact_number,
            },
      });
      setBilling((prev) => ({ ...prev, ...res.data }));
      setBillingConfirmed(true);
      toast.success('Billing person confirmed.');
      setExpandedStep('step3');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save billing person');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleConfirmSign = async () => {
    if (!signatureName.trim()) {
      toast.error('Type your name to sign.');
      return;
    }
    if (!isNonNcsuPayer) {
      if (buildAccountsPayload().length === 0) {
        toast.error('Select at least one account before signing.');
        return;
      }
      if (Math.abs(allocatedTotal - myShareAmount) > 0.01) {
        toast.error(`Account amounts must total your share of $${myShareAmount.toFixed(2)}.`);
        return;
      }
    }

    setSigning(true);
    try {
      const res = await axios.post(
        `/api/invoices/${projectRef}/${invoiceId}/distribution/confirm-sign`,
        {
          signature_name: signatureName.trim(),
          accounts: buildAccountsPayload(),
        }
      );
      toast.success(res.data?.message || 'Signed.');
      if (onUpdated) await onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const handleUnsign = async () => {
    setUnsigning(true);
    try {
      const res = await axios.post(
        `/api/invoices/${projectRef}/${invoiceId}/distribution/unsign`
      );
      toast.success(res.data?.message || 'Signature removed.');
      setBillingConfirmed(true);
      if (onUpdated) await onUpdated();
      setExpandedStep('step1');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove signature');
    } finally {
      setUnsigning(false);
    }
  };

  // ── Signing progress (visible to all payers) ──
  const renderSigningStatus = () => {
    if (!splitIsSet) return null;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Signing progress
          </Typography>
          <Chip
            size="small"
            label={`${signedCount} of ${existingPayers.length} signed`}
            color={allSigned ? 'success' : 'default'}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={(signedCount / existingPayers.length) * 100}
          color={allSigned ? 'success' : 'primary'}
          sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
        />
        <Stack spacing={1}>
          {existingPayers.map((p, i) => (
            <Box
              key={i}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {p.confirmed ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <HourglassEmptyIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2">
                  {p.display_name}
                  {idOf(p.user_id) === currentUserId ? ' (you)' : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  {p.percent_share}% · ${roundTo(p.amount_share).toFixed(2)}
                </Typography>
                {p.confirmed && p.signature?.signed_at && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                    Signed {formatDateTime(p.signature.signed_at)}
                  </Typography>
                )}
                {!p.confirmed && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                    Awaiting signature
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </Paper>
    );
  };

  const renderSignedSummary = () => (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Payment Distribution
      </Typography>
      <Alert severity="success" sx={{ mb: 2 }}>
        All payers have signed. This invoice is finalized and has been submitted to the business office.
      </Alert>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Payer</TableCell>
              <TableCell align="right">Share</TableCell>
              <TableCell>Accounts</TableCell>
              <TableCell>Signature</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {existingPayers.map((p, i) => (
              <TableRow key={i}>
                <TableCell>{p.display_name}</TableCell>
                <TableCell align="right">
                  {p.percent_share}% (${roundTo(p.amount_share).toFixed(2)})
                </TableCell>
                <TableCell>
                  {p.is_ncsu === false
                    ? 'Non-NCSU'
                    : (p.accounts || [])
                        .map((a) => `${displayAccountNumber(a.account_number)}: $${roundTo(a.amount).toFixed(2)}`)
                        .join(', ') || '—'}
                </TableCell>
                <TableCell>
                  {p.signature?.name ? (
                    <Box>
                      <Typography variant="body2">{p.signature.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(p.signature.signed_at)}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip label="Pending" size="small" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // ── Step 1 content: designate payers (PI) + choose your accounts ──
  const renderPISplit = () => {
    const showEditor = !splitIsSet || editingSplit;
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Who pays, and how much
          </Typography>
          {splitIsSet && !editingSplit && !allSigned && (
            <Button size="small" onClick={() => setEditingSplit(true)}>
              Edit split
            </Button>
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Choose which PI / Co-PI will pay and their percentage (must total 100%). You can change the
          split until every payer has signed — changing someone&apos;s percentage clears their
          signature so they re-confirm.
        </Typography>

        {!showEditor ? (
          <Stack spacing={0.5}>
            {existingPayers.map((p, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{p.display_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {p.percent_share}% · ${roundTo(p.amount_share).toFixed(2)}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Payer?</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Percent %</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payers.map((p) => (
                    <TableRow key={p.user_id} selected={p.selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={p.selected}
                          onChange={() => handleTogglePayer(p.user_id)}
                        />
                      </TableCell>
                      <TableCell>{p.display_name}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={p.percent_share}
                          onChange={(e) => handlePercentChange(p.user_id, e.target.value)}
                          disabled={!p.selected}
                          sx={{ width: 90 }}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${roundTo(((parseFloat(p.percent_share) || 0) / 100) * total).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
              <Typography
                variant="body2"
                color={Math.abs(totalPercent - 100) > 0.01 ? 'error' : 'success.main'}
              >
                Total: {totalPercent}%
              </Typography>
              <Stack direction="row" spacing={1}>
                {splitIsSet && editingSplit && (
                  <Button onClick={() => setEditingSplit(false)} disabled={savingSplit}>
                    Cancel
                  </Button>
                )}
                <Button variant="contained" onClick={handleSaveSplit} disabled={savingSplit}>
                  {savingSplit ? 'Saving...' : splitIsSet ? 'Update Split' : 'Save Split'}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Paper>
    );
  };

  const renderMyAccounts = () => {
    if (!splitIsSet) {
      return (
        <Alert severity="info">
          {isPI
            ? 'Save the payment split above, then choose the accounts for your own share.'
            : 'Waiting for the PI to set up the payment split.'}
        </Alert>
      );
    }
    if (!myPayer) {
      return (
        <Alert severity="info">
          You are not a designated payer on this invoice, so there is nothing for you to sign.
        </Alert>
      );
    }
    if (isNonNcsuPayer) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your share is {myPayer.percent_share}% (${myShareAmount.toFixed(2)}). As a non-NCSU payer
            you have no NC State account to select.
          </Alert>
          <Button variant="contained" fullWidth onClick={() => setExpandedStep('step2')}>
            Continue
          </Button>
        </Box>
      );
    }

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Your share: {myPayer.percent_share}% (${myShareAmount.toFixed(2)})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tap an account to select it. Amounts must total ${myShareAmount.toFixed(2)}.
        </Typography>

        {loadingAccounts ? (
          <CircularProgress size={20} />
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {myAccounts.map((acct, i) => {
              const selected = isAllocated(acct.number);
              return (
                <Chip
                  key={i}
                  label={`${displayAccountNumber(acct.number)} (${acct.source})`}
                  onClick={() => handleAddAllocation(acct)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  icon={selected ? <CheckCircleIcon /> : undefined}
                  size="small"
                />
              );
            })}
            {myAccounts.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No saved accounts. Add one below.
              </Typography>
            )}
          </Stack>
        )}

        {myAllocations.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {myAllocations.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{displayAccountNumber(a.account_number)}</TableCell>
                    <TableCell>{a.source}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={a.amount}
                        onChange={(e) => handleAllocationAmountChange(i, e.target.value)}
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label="Delete" size="small" onClick={() => handleRemoveAllocation(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}
        >
          <Typography
            variant="body2"
            color={Math.abs(allocatedTotal - myShareAmount) > 0.01 ? 'error' : 'success.main'}
          >
            Allocated: ${allocatedTotal.toFixed(2)} of ${myShareAmount.toFixed(2)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600 }}
            color={Math.abs(remainingShare) < 0.01 ? 'success.main' : 'warning.main'}
          >
            Remaining: ${remainingShare.toFixed(2)}
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Add a new account (saved to your profile)
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.25 }}>
            <Grid item xs={5}>
              <TextField
                size="small"
                fullWidth
                label="Account #"
                value={newAcctNumber}
                onChange={(e) => setNewAcctNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                size="small"
                fullWidth
                label="Source of funds"
                value={newAcctSource}
                onChange={(e) => setNewAcctSource(e.target.value)}
              />
            </Grid>
            <Grid item xs={2}>
              <Button fullWidth variant="outlined" onClick={handleAddNewAccount} sx={{ height: '100%' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Button variant="contained" onClick={handleSaveMyShare} disabled={savingShare} fullWidth>
          {savingShare ? 'Saving...' : 'Save Accounts & Continue'}
        </Button>
      </Paper>
    );
  };

  // ── Step 2 content: the billing person ──
  const renderBillingStep = () => {
    const bc = billing?.billing_contact || {};
    const self = billing?.self || {};
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The person the business office should contact about paying this invoice. They take no part
          in the invoicing process and are only listed on the billing request email.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={billingSelf}
                onChange={(e) => handleToggleBillingSelf(e.target.checked)}
              />
            }
            label="I am my own billing person"
          />

          {billingSelf ? (
            <Stack spacing={0.5} sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {self.name || 'Not provided'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {self.email || 'Not provided'}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {displayPhoneNumber(self.contact_number)}
              </Typography>
            </Stack>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.25 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Billing person name"
                  value={bc.name || ''}
                  onChange={(e) => handleBillingFieldChange('name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Billing person email"
                  value={bc.email || ''}
                  onChange={(e) => handleBillingFieldChange('email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Billing person phone</Typography>
                <PhoneInput
                  value={bc.contact_number || ''}
                  onChange={(v) => handleBillingFieldChange('contact_number', v)}
                />
              </Grid>
            </Grid>
          )}
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Saved to your profile, so you only need to set this once.
        </Typography>

        <Button
          variant="contained"
          onClick={handleSaveBillingContact}
          disabled={savingBilling || !billing}
          fullWidth
        >
          {savingBilling ? 'Saving...' : 'Confirm Billing Person & Continue'}
        </Button>
      </Box>
    );
  };

  // ── Step 3 content: review and sign ──
  const renderSignStep = () => {
    if (myPayer?.confirmed) {
      return (
        <Box>
          <Alert severity="success" sx={{ mb: 1.5 }}>
            You confirmed your accounts and signed on{' '}
            {formatDateTime(myPayer.signature?.signed_at)}.
          </Alert>
          {allSigned ? (
            <Typography variant="body2" color="text.secondary">
              Every payer has signed. The billing request has been sent to the business office.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Signatures can still be changed because not everyone has signed yet. Remove your
                signature if you need to change your accounts{isPI ? ' or the split' : ''}.
              </Typography>
              <Button variant="outlined" color="warning" onClick={handleUnsign} disabled={unsigning}>
                {unsigning ? 'Removing...' : 'Remove my signature to make changes'}
              </Button>
            </>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f7f9fc' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            What you are signing for
          </Typography>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Your share</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {myPayer?.percent_share}% · ${myShareAmount.toFixed(2)}
              </Typography>
            </Box>
            {savedAccounts.map((a, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {displayAccountNumber(a.account_number)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${roundTo(a.amount).toFixed(2)}
                </Typography>
              </Box>
            ))}
            {isNonNcsuPayer && (
              <Typography variant="body2" color="text.secondary">
                Non-NCSU payer — no account to charge.
              </Typography>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Billing contact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {billing?.resolved?.name || '—'}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Alert severity="warning" sx={{ mb: 2 }}>
          The accounts you select will be <strong>final</strong>. Once <strong>all</strong> designated
          payers have signed, this invoice, its accounts, and the distribution become final and are
          sent to the business office for payment. Please be absolutely sure before you sign.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fafafa' }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            {CERTIFICATION_TEXT}
          </Typography>
        </Paper>

        <TextField
          fullWidth
          label="Signature (type your full name)"
          value={signatureName}
          onChange={(e) => setSignatureName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirmSign}
          disabled={signing || !signatureName.trim()}
          fullWidth
        >
          {signing ? 'Signing...' : 'Confirm Accounts & Sign'}
        </Button>
      </Box>
    );
  };

  // ── Accordion assembly ──
  const step1Summary = !splitIsSet
    ? 'Not started'
    : !myPayer
    ? `${existingPayers.length} payer${existingPayers.length === 1 ? '' : 's'} designated`
    : isNonNcsuPayer
    ? `Your share: ${myPayer.percent_share}% · no account needed`
    : accountsSaved && !hasUnsavedAllocations
    ? `${savedAccounts.length} account${savedAccounts.length === 1 ? '' : 's'} · $${myShareAmount.toFixed(2)}`
    : hasUnsavedAllocations && savedAccounts.length > 0
    ? 'Unsaved changes'
    : `Your share: ${myPayer.percent_share}% · $${myShareAmount.toFixed(2)}`;

  const step2Summary = step2Done
    ? billing?.resolved?.name || 'Confirmed'
    : step1Done
    ? 'Confirm who the business office should contact'
    : 'Complete step 1 first';

  const step3Summary = step3Done
    ? `Signed by ${myPayer?.signature?.name}`
    : step2Done
    ? 'Review and sign'
    : 'Complete the previous steps first';

  const makeStep = (id, index, title, summary, done, locked, content) => (
    <Accordion
      expanded={expandedStep === id}
      onChange={() => !locked && setExpandedStep(expandedStep === id ? '' : id)}
      disabled={locked}
      disableGutters
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: done ? 'success.light' : expandedStep === id ? 'primary.light' : 'divider',
        '&:before': { display: 'none' },
        boxShadow: expandedStep === id ? 2 : 0,
        opacity: locked ? 0.7 : 1,
      }}
    >
      <AccordionSummary expandIcon={locked ? null : <ExpandMoreIcon />} sx={{ px: 2 }}>
        <StepHeader index={index} title={title} summary={summary} done={done} locked={locked} />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2.5, pt: 0 }}>{content}</AccordionDetails>
    </Accordion>
  );

  // A PI who did not designate themselves as a payer only manages the split -
  // there is nothing for them to allocate or sign, so hide the later steps.
  const hasNothingToSign = splitIsSet && !myPayer;

  const renderSteps = () => (
    <>
      {makeStep(
        'step1',
        1,
        hasNothingToSign ? 'Payers & split' : 'Payers & accounts',
        step1Summary,
        step1Done || hasNothingToSign,
        false,
        <>
          {isPI && renderPISplit()}
          {renderMyAccounts()}
        </>
      )}
      {!hasNothingToSign && (
        <>
          {makeStep(
            'step2',
            2,
            'Billing person (Invoice To)',
            step2Summary,
            step2Done,
            !step1Done,
            renderBillingStep()
          )}
          {makeStep(
            'step3',
            3,
            'Review & sign',
            step3Summary,
            step3Done,
            !step2Done,
            renderSignStep()
          )}
        </>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle>
        Invoice #{invoiceId?.split('_INV')[1] || invoiceId} — Payment Distribution
        {workflowStatus && (
          <Chip
            label={workflowStatus === 'Signed' ? 'Billed & Signed' : workflowStatus}
            size="small"
            color={workflowStatus === 'Signed' ? 'success' : 'info'}
            sx={{ ml: 1 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Left: payment management */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{ p: 2.5, overflowY: 'auto', maxHeight: { md: 'calc(90vh - 130px)' } }}
          >
            {readOnly ? (
              renderSignedSummary()
            ) : (
              <>
                {renderSigningStatus()}
                {renderSteps()}
              </>
            )}
          </Grid>

          {/* Right: team discussion */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              borderLeft: { md: '1px solid' },
              borderTop: { xs: '1px solid', md: 'none' },
              borderColor: { xs: 'divider', md: 'divider' },
              backgroundColor: '#f7f9fc',
              display: 'flex',
              flexDirection: 'column',
              p: 2.5,
              maxHeight: { md: 'calc(90vh - 130px)' },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Project Chat
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Discuss this invoice and the payment split with your teammates here.
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ProjectChat
                projectId={project?._id}
                user={user}
                channel="team_chat"
                projectUsers={project?.users || []}
                maxHeight="calc(90vh - 240px)"
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDistributionDialog;
